import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChildren,
    QueryList
} from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TextareaComponent } from '@epd/pattern-library';

import { KeyCode } from '@app/enums';
import { ChatStorageService, LeftNavigationService } from '@craig/core';
import { ChatMessage, ChatBotResponse } from '@app/interfaces';
import { ChatService } from '../../services/chat/chat.service';
import { OutputHelper } from '../../interfaces/output-helper.interface';
import { OutputHelpersService } from '../../services/output-helpers/output-helpers.service';

@Component({
    selector: 'craig-chat-view',
    templateUrl: './chat-view.component.html',
    styleUrls: ['./chat-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatViewComponent implements OnInit, AfterViewInit, OnDestroy {
    chatShowed: boolean = false;
    chatQuery: string = '';
    isSubmitInProgress: boolean = false;

    @ViewChildren('queryInput') queryInputList!: QueryList<TextareaComponent>;

    private readonly componentDestroyed$: Subject<boolean> = new Subject();
    private readonly queryAborted$: Subject<boolean> = new Subject();
    private queryInputComponent?: TextareaComponent;

    constructor(
        private readonly leftNavigationService: LeftNavigationService,
        private readonly chatService: ChatService,
        private readonly chatStorageService: ChatStorageService,
        private readonly outputHelpersService: OutputHelpersService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.leftNavigationService
            .chatShowedChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((showChat: boolean) => {
                if (showChat) {
                    this.prepareForNewChat();
                    this.chatStorageService.addNewChat();
                } else {
                    this.chatShowed = false;
                    this.leftNavigationService.selectChatSession(null);
                }

                if (this.isSubmitInProgress) {
                    this.abortQuery();
                }

                this.changeDetectorRef.detectChanges();
            });

        this.leftNavigationService
            .chatSessionSelectedChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((sessionId: number | null) => {
                if (sessionId) {
                    this.prepareForNewChat();

                    if (this.isSubmitInProgress) {
                        this.abortQuery();
                    }

                    this.chatStorageService.addNewChat(sessionId);
                    this.changeDetectorRef.detectChanges();
                }
            });

        this.outputHelpersService
            .selectedOutputHelpersOptionChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(({ isChecked, query: prompt }: OutputHelper) => {
                let existingPrompt: string = this.chatQuery.trim();

                if (isChecked) {
                    existingPrompt = existingPrompt ? `${existingPrompt} ${prompt}` : prompt;
                } else {
                    existingPrompt = this.chatQuery.replace(prompt, '');
                }

                this.chatQuery = existingPrompt.trim();

                this.focusQueryInput();

                this.changeDetectorRef.detectChanges();
            });

        this.chatService
            .responseLoadingChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((loading: boolean) => {
                this.isSubmitInProgress = loading;

                this.changeDetectorRef.detectChanges();
            });
    }

    ngAfterViewInit(): void {
        this.queryInputList.changes.subscribe(() => {
            this.queryInputComponent = this.queryInputList.first;
        });
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next(true);
    }

    onChatBoxKeydown(e: Event): void {
        const { key, code, shiftKey } = e as KeyboardEvent;
        const keyCode: string = key || code;

        if (keyCode === KeyCode.Enter && !shiftKey) {
            e.preventDefault();

            if (!this.isSubmitInProgress) {
                this.submit();
            }
        }
    }

    onChatBoxChange(value: string): void {
        this.chatQuery = value;

        if (this.chatQuery === '') {
            this.outputHelpersService.clearOutputHelperOptions();
        }
    }

    handlePromptUpdate(newSamplePromptText: string): void {
        this.chatQuery = newSamplePromptText;
        this.focusQueryInput();
        this.outputHelpersService.clearOutputHelperOptions();
    }

    submit(): void {
        if (!!this.chatQuery.trim()) {
            this.chatService.showResponseLoader();

            const userMessage: ChatMessage = this.chatService.addUserMessage(this.chatQuery);

            this.chatService
                .sendQuery(userMessage)
                .pipe(
                    takeUntil(this.queryAborted$),
                    finalize(() => this.chatService.hideResponseLoader())
                )
                .subscribe({
                    next: (assistantResponse: ChatBotResponse | null) => {
                        this.chatStorageService.addMessageToChatHistory(userMessage);

                        if (assistantResponse) {
                            const assistantMessage: ChatMessage = this.chatService.addAssistantMessage(
                                assistantResponse.message.content,
                                assistantResponse.request_id
                            );

                            this.chatStorageService.addMessageToChatHistory(assistantMessage);
                        }
                    },
                    error: (error: Error) => {
                        if (error.message) {
                            this.chatService.addAssistantMessage(error.message);
                        }
                    }
                });
        }

        // Clearing of the chat box can be done even if the response is bad.
        this.clearChatQuery();
    }

    clearChatQuery(): void {
        this.chatQuery = '';

        this.outputHelpersService.clearOutputHelperOptions();
    }

    abortQuery(): void {
        this.queryAborted$.next(true);
    }

    private focusQueryInput(): void {
        if (this.queryInputComponent) {
            this.queryInputComponent.embeddedInput.nativeElement.focus();
        }
    }

    private prepareForNewChat(): void {
        if (!this.chatShowed) {
            this.chatShowed = true;
        }

        this.clearChatQuery();
    }
}
